import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { useUpdateApplication } from '../../hooks/queries';
import BaseCard from './BaseCard';
import { MultipleDMSDialog } from '../Dialogs/MultipleDMSDialog';
import React, {useState} from 'react';
import { CNumber } from '../../types';
import get from 'lodash/get';
import { IOktaContext } from '@okta/okta-react/bundles/types/OktaContext';
import { useOktaAuth } from '@okta/okta-react';
import { getDMSProxyFirewallUrl } from '../../util/getDMSProxyUrls';
import { AMPLITUDE_EVENTS } from '../../constants';
import {driveLaunchFallback} from '../../util/driveLaunchFallback';
import { useDriveCNumbersStore, useAdoptEaseStore, useAllApplicationsStore } from '../../Store';
import { updateAdoptEaseWorkflowsBeforeDriveLaunch } from '../../util/AdoptEaseUtils';
import AdoptEaseDialog from '../AdoptEase/AdoptEaseDialog';
import { setDocumentTitle } from '../../util/TitleManager';
import useAudit from '../../hooks/useAudit';
import { useUnifyProfileStore, useStartupToastStore, useApplicationStore, useWorkflowsStore } from '../../Store';
import SetStartupAppDialog from '../Dialogs/SetStartupAppDialog';
import { useUpdateDefaultApplication } from '../../hooks/queries';
import { canOpenDrive } from '../../util/DriveUtils';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
const DMSCNumber = 0;

interface Props {
  id: string;
  isEnabled: boolean;
  isFavorite?: boolean;
  description: string;
  name: string;
  iconColor: string;
  iconText: string;
  url: string;
  onCdkuOpen: Function;
  cdkuUrl: string;
  isRestricted:boolean;
  restrictedTooltipMsg:string;
  isDefault?: boolean;
}

export default function DriveApplicationCard({
  id,
  isFavorite,
  isEnabled,
  description,
  name,
  iconColor,
  iconText,
  onCdkuOpen,
  cdkuUrl = '',
  isRestricted,
  restrictedTooltipMsg,
  isDefault = false,
}: Props) {
  /**
   * hooks
   */
  const { mutate } = useUpdateApplication();
  const session: IOktaContext = useOktaAuth();

  const authToken = get(session, 'authState.idToken.idToken', '');
  const dmsBoxes = useDriveCNumbersStore((state: any) => state.dmsBoxMapping);
  const dmsCNumbers = useDriveCNumbersStore((state: any) => state.cNumbers);
  const isFetching = useDriveCNumbersStore((state: any) => state.isFetched);
  const [shouldMultipleDMSDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const { trackEvent } = useAnalytics();
  const userLocale = localStorage.getItem('userLocale') || 'en_US';
  const [showStartupDialog, setShowStartupDialog] = useState(false);
  const { unifyProfile } = useUnifyProfileStore();
  const { mutate: setDefaultAppMutation } = useUpdateDefaultApplication();
  const { showStartupToast } = useStartupToastStore();
  const { t } = useTranslation();
  const applications = useApplicationStore((state: any) => state.applications);
  const allApplications = useAllApplicationsStore((state: any) => state.applications);
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');

  const { url } = getDMSProxyFirewallUrl({
    cNumber: get(dmsCNumbers, [DMSCNumber, 'cNumber']),
    driveToken: authToken,
    lang:userLocale,
  });

  //AdoptEase
  const adoptEaseConfigData = useAdoptEaseStore((state:any) => state.adoptEaseConfigData);
  const adoptEaseUsageData = useAdoptEaseStore((state:any) => state.adoptEaseUsageData);
  const setIAdoptEaseWorkflows = useAdoptEaseStore((state:any) => state.setIAdoptEaseWorkflows);
  const adoptEaseWorkflows = useAdoptEaseStore((state:any) => state.adoptEaseWorkflows);
  const [showRecommendation, setShowRecommendation] = useState(false);
  const { captureEvent} = useAudit();

  const handleOpenDrive = async () => {
    setDocumentTitle(name, cdkuUrl);
    captureEvent({action:"Opened",type:"Application", name});

    const workflows = updateAdoptEaseWorkflowsBeforeDriveLaunch(adoptEaseWorkflows, adoptEaseUsageData, adoptEaseConfigData)

    if(workflows && workflows.length !== 0)
    {
      setIAdoptEaseWorkflows(workflows)
      setShowRecommendation(true);
    }
    else{
      onProceed();
    }
  };

  const onProceed = async (isChildTrigger: boolean = false) => {

    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
      Application: name,
    });

    if ((dmsCNumbers?.length as unknown as number) > 1) {
      return setDialogOpen(true);
    }

    const dmsDataWithName = dmsCNumbers?.map((cNumber: any) => {
      const dmsBoxAliasName = dmsBoxes?.dmsBoxMappings
        .find((eachBoxDetails: any) => (eachBoxDetails?.cnumber as string).toLowerCase() === (cNumber.cNumber).toLowerCase())
        ?.alias || '';
      return {
        'data-testid': cNumber.cNumber,
        label: `${cNumber.cNumber} ${dmsBoxAliasName && `- ${dmsBoxAliasName}`}`,
        value: cNumber.cNumber,
        link: `https://${cNumber.cNumber}-dms.drive.connectcdk.com/apps/autoTools/cds/osd/osd.php`,
        dmsUserId: cNumber.dmsUserId,
        dmsBoxAliasName: dmsBoxAliasName.toLowerCase()
      };
    });

    if (isChildTrigger) {
      trackEvent(AMPLITUDE_EVENTS.OPEN_ADAPTIVA_OSD_SINGLE_DMS_USER, {
        'C-Number': dmsDataWithName[0]?.value,
        'Name': dmsDataWithName[0]?.label
      });
      return dmsDataWithName[0].link;
    }

    let driveLaunchResponse;
   
    driveLaunchResponse = await driveLaunchFallback(url);

    console.log(driveLaunchResponse,':drive launch');

    trackEvent(AMPLITUDE_EVENTS.OPEN_DRIVE, {
      'C-Number': dmsDataWithName[0]?.value, 'Name' : dmsDataWithName[0]?.label
    });
    return setDialogOpen(false);

  };

  const onFavoriteClick = () => {
    mutate({ id, isFavorite: Boolean(!isFavorite) });
    trackFavoriteChange(!isFavorite);
  };

  const trackFavoriteChange = (isFavorite: boolean) => {
    isFavorite &&
      trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
        'Favorite Application': name,
      });
  };

  const setDefaultApp = () => {
    setShowStartupDialog(true);
  };

  const currentDefaultName = (() => {
    if (!unifyProfile?.apps) return '';

    // Check for existing default app (different from this one)
    const defaultApp = unifyProfile.apps.find(
      (app: any) =>
        (app.isDefaultApp === true ) &&
        (app.appId) !== id
    );
    if (defaultApp) {
      const appId = defaultApp.appId;
      const appDetails =
        applications?.find((a: any) => a.id === appId) ||
        allApplications?.flat().find((a: any) => a.appId === appId);
      return appDetails?.name || '';
    }

    // Check for existing default workflow
    const defaultWorkflow = unifyProfile.workflows?.find(
      (wf: any) => wf.isDefaultWorkflow === true
    );
    if (defaultWorkflow) {
      const wfId = defaultWorkflow.workflowId;
      return workflows?.find((wf: any) => wf.id === wfId)?.name || '';
    }

    return '';
  })();

  const getDialogScenario = (): 'set' | 'remove' | 'update' => {
    if (isDefault) return 'remove';
    if (currentDefaultName) return 'update';
    return 'set';
  };

  const confirmDefaultApp = () => {
    const settingAsDefault = !isDefault;
    setDefaultAppMutation(
      { id, isDefault: settingAsDefault },
      {
        onSuccess: () => {
          trackEvent(AMPLITUDE_EVENTS.SET_STARTUP, {
            'Application': name,
            'Action': settingAsDefault ? 'Set' : 'Remove',
          });
          if (settingAsDefault) {
            showStartupToast(t('baseCard.startupApp.toastSet', { appName: name }), 'positive');
          } else {
            showStartupToast(t('baseCard.startupApp.toastRemove', { appName: name }), 'positive');
          }
        },
        onError: () => {
          showStartupToast(t('baseCard.startupApp.toastError'), 'negative');
        },
      }
    );
    setShowStartupDialog(false);
  };

  return (
    <>
      {shouldMultipleDMSDialogOpen && (
        <MultipleDMSDialog
          setDialogOpen={setDialogOpen}
          shouldDialogOpen={shouldMultipleDMSDialogOpen}
          dmsCNumbers={dmsCNumbers as CNumber[]}
          isFetching={isFetching}
        />
      )}
      <BaseCard
        onOpenClick={handleOpenDrive}
        name={name}
        iconColor={iconColor}
        description={description}
        iconText={iconText}
        isEnabled={isEnabled}
        isFavorite={isFavorite}
        onFavoriteClick={onFavoriteClick}
        isIconRequired={true}
        onCdkuOpen={onCdkuOpen}
        cdkuUrl={cdkuUrl}
        isRestricted={isRestricted}
        restrictedTooltipMsg={restrictedTooltipMsg}
        id={id}
        dmsCheck={onProceed}
        isDriveApp={true}
        setDefaultApp={setDefaultApp}
        isDefault={isDefault}
        showStartupOption={url.includes("drive") && canOpenDrive() && Boolean(startupAppFlag)}
      />
      <SetStartupAppDialog
        isOpen={showStartupDialog}
        appName={name}
        scenario={getDialogScenario()}
        previousDefaultAppName={currentDefaultName}
        onConfirm={confirmDefaultApp}
        onDismiss={() => setShowStartupDialog(false)}
      />
      {showRecommendation && <AdoptEaseDialog
        isOpen={true}
        onProceed={onProceed}
        onClose={() => setShowRecommendation(false)} />
      }
    </>
  );
}
